<template>
    <div v-if="hasAccess" class="py-4 container-fluid">
			<ToastPrime position="bottom-left" />
			<div class="card border-0" style="height: calc(100vh - 120px); display: flex; flex-direction: column;">

				<!-- HEADER -->
				<div class="card-header py-3 d-flex flex-wrap align-items-center gap-3">
					<h5 class="mb-0">{{ pageTitle }}</h5>

					<div class="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
						<IconField class="search-field">
							<InputIcon>
									<i class="pi pi-search" />
							</InputIcon>

							<InputText v-model="table.search" type="text" size="small" :placeholder="`Search ${searchLabel}`" class="rounded-pill" style="min-width: 500px;" @keyup="filter" />
						</IconField>
					</div>

					<div class="d-flex flex-wrap gap-2">
						<SelectPrime v-if="hasClientData" v-model="table.clientFilter" :options="clientFilterOptions" option-label="label" option-value="value" placeholder="Filter Client" class="rounded-pill" size="small" style="width: auto;">
							<template #value="slotProps">
								<div class="d-flex align-items-center">
									<i class="pi pi-filter me-2" :class="slotProps.value ? 'text-primary' : 'text-muted'" style="font-size: 0.8rem;" />
									<div :class="{ 'text-muted': !slotProps.value }">
										{{ slotProps.value || slotProps.placeholder }}
									</div>
								</div>
							</template>
						</SelectPrime>

						<SelectPrime v-if="hasCompanyData" v-model="table.companyFilter" :options="companyFilterOptions" option-label="label" option-value="value" placeholder="Filter Company" class="rounded-pill" size="small" style="width: auto;">
							<template #value="slotProps">
								<div class="d-flex align-items-center">
									<i class="pi pi-filter me-2" :class="slotProps.value ? 'text-primary' : 'text-muted'" style="font-size: 0.8rem;" />
									<div :class="{ 'text-muted': !slotProps.value }">
										{{ slotProps.value || slotProps.placeholder }}
									</div>
								</div>
							</template>
						</SelectPrime>

						<SelectPrime v-if="hasPlantData" v-model="table.plantFilter" :options="plantFilterOptions" option-label="label" option-value="value" placeholder="Filter Plant" class="rounded-pill" size="small" style="width: auto;">
								<template #value="slotProps">
										<div class="d-flex align-items-center">
												<i class="pi pi-filter me-2" :class="slotProps.value ? 'text-primary' : 'text-muted'" style="font-size: 0.8rem;" />
												<div :class="{ 'text-muted': !slotProps.value }">
														{{ slotProps.value || slotProps.placeholder }}
												</div>
										</div>
								</template>
						</SelectPrime>

						<button type="button" class="btn btn-sm btn-primary m-0" @click="openImportDialog">
							<i class="pi pi-upload me-1"></i>
							Import
						</button>

						<button type="button" class="btn btn-sm btn-primary m-0" :disabled="exporting" @click="exportData">
							<i v-if="exporting" class="fas fa-spinner fa-spin me-1"></i>
							<i v-else class="pi pi-download me-1"></i>
							Export
						</button>
					</div>
				</div>

				<!-- TABLE -->
				<div style="flex: 1; overflow-y: auto;">
					<DataTable :value="table.onLoad ? dummyRows : displayData" removable-sort scrollable scroll-height="flex" table-style="min-width: 100%; width: max-content;">

						<Column v-for="col in pageColumns" :key="col.field" :field="col.field" :header="col.header" sortable header-class="bg-light text-dark">
							<template v-if="table.onLoad" #body>
								<Skeleton />
							</template>

							<template v-else #body="slotProps">
								{{ renderCell(slotProps.data, col) }}
							</template>
						</Column>

						<!-- EMPTY -->
						<template #empty>
							<div style="text-align: center; padding: 40px 0;">
								<img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7;" />
								<div style="color:#888; font-size:1.1em; font-weight:500;">
									No Data
								</div>
							</div>
						</template>

					</DataTable>
				</div>

				<!-- FOOTER -->
				<div class="card-footer pb-0 d-flex justify-content-between align-items-center">
					<BasePagination v-model="table.page.current_pages" :page-count="table.page?.total_pages || 1" size="sm" @click="changePage()" />
					<p> {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }} </p>
				</div>

			</div>

			<!-- Dialog Import Excel (Reusable, digerakkan Vuex module importExcel).
			     Template & import service pakai slug resource aktif (pageResource). -->
			<ImportExcelModal
				module="importExcel"
				width="50rem"
				:import-id="pageResource"
				:import-service="importServiceFn"
				:template-service="templateServiceFn"
				:show-success-inline="false"
				@close="onImportClose"
				@success="onImportSuccess"
			/>
    </div>
    <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import NoAccess from "@/components/NoAccess.vue";
import ImportExcelModal from "@/components/ImportExcelModal.vue";
import moment from "moment";
import api from "@/helpers/api";
import generalMasterDataService from "@/services/generalMasterData/generalMasterDataService";
import clientService from "@/services/generalMasterData/clientService";
import companiesService from "@/services/generalMasterData/companiesService";

// Mapping resource (slug dari route params) -> permission key, 
// disalin persis dari kondisi hasAnyPermission per sidenav-item di SidenavList.vue (menu General Master Data).
const RESOURCE_PERMISSION_MAP = {
    'abc-indicators': 'abc-indicator-view',
    'account-assignment-groups': 'account-assignment-group-view',
    'gm-availibility-checks': 'availibility-check-view',
    'bill-of-material': 'bom-view',
    'distribution-channels': 'distribution-channel-view',
    'divisions': 'division-view',
    'equipment': 'equipment-view',
    'gm-individual-or-collectives': 'individual-or-collective-view',
    'item-category-groups': 'item-cat-group-view',
    'loading-groups': 'loading-group-view',
    'lot-sizes': 'lot-size-view',
    'manref-types': 'manref-type-view',
    'material-group': 'material-group-view',
    'material-type': 'material-type-view',
    'mrp-controllers': 'mrp-controller-view',
    'gm-mrp-groups': 'mrp-group-view',
    'mrp-types': 'mrp-type-view',
    'plant': 'plant-view',
    'gm-plant-sp': 'plant-sp-view',
    'price-controls': 'price-control-view',
    'procurement-types': 'proc-type-view',
    'product-hierarchies': 'product-hierarchy-view',
    'profit-centers': 'profit-center-view',
    'purchasing-groups': 'purchasing-group-view',
    'requirement-groups': 'requirement-group-view',
    'sales-organizations': 'sales-organization-view',
    'source-types': 'source-type-view',
    'special-procurement-types': 'sproctype-view',
    'gm-storage-location-for-eps': 'storage-location-for-ep-view',
    'storage-locations': 'storage-location-view',
    'gm-tax-categories': 'tax-category-view',
    'gm-tax-classifications': 'tax-class-view',
    'transportation-groups': 'trans-group-view',
    'uom': 'uom-view',
    'valuation-categories': 'valuation-category-view',
    'valuation-classes': 'valuation-class-view',
    'valuation-types': 'valuation-type-view',
    'account-category-references': 'account-category-reference-view',
    'service-categories': 'service-category-view',
    'service-groups': 'service-group-view',
    'warehouses': 'warehouse-view',
};

const UNIVERSAL_COLUMNS = [
    { field: "client.code", header: "Client" },
    { field: "client", header: "Client" },
    { field: "client_code", header: "Client" },
    { field: "company", header: "Company" },
    { field: "company_code", header: "Company" },
    { field: "bom_key", header: "BOM Key" },
    { field: "parent", header: "Parent" },
    { field: "code", header: "Code" },
    { field: "name", header: "Name" },
    { field: "plant", header: "Plant" },
    { field: "plant_code", header: "Plant" },
    { field: "level", header: "Level" },
    { field: "type", header: "Type" },
    { field: "serial_number", header: "Serial Number" },
    { field: "manufacturer", header: "Manufacturer" },
    { field: "additional_data", header: "Additional Data" },
    { field: "definition", header: "Definition" },
    { field: "maintenance_status", header: "Maintenance Status" },
    { field: "account_category_ref", header: "Account Category Ref" },
    { field: "price_control", header: "Price Control" },
    { field: "valuation_category", header: "Valuation Category" },
    { field: "acc_cat_ref", header: "Acc Cat Ref" },
    { field: "city", header: "City" },
    { field: "address", header: "Address" },
    { field: "po_box", header: "PO Box" },
    { field: "po_postal_code", header: "PO Postal Code" },
    { field: "description", header: "Description" },
    { field: "created_by", header: "Created By", fallback: "-" },
    { field: "updated_by", header: "Updated By", fallback: "-" },
];

export default {
    name: "GeneralMasterData",

    components: {
        BasePagination,
        NoAccess,
        ImportExcelModal,
    },

    props: {
        resource: {
            type: String,
            default: ""
        }
    },

    data() {
        return {
						clientCode: '',
						companyCode: '',
            table: {
                onLoad: true,
                search: this.$route.query.search || "",
                clientFilter: this.$route.query.client || null,
                companyFilter: this.$route.query.company || null,
                plantFilter: this.$route.query.plant || null,
                clientOptions: [],
                companyOptions: [],
                plantOptions: [],
                knownColumns: [],
                total: 0,
                data: [],
                limit: 100,
                page: {
                    current_pages: 1,
                    per_pages: 100,
                    total_pages: 1,
                },
                loadTimeout: null,
            },
            dummyRows: Array.from({ length: 10 }, () => ({})),
            exporting: false,
            importSuccessHandled: false,
        };
    },

    computed: {
        ...mapState({
            authUserPermissions: (state) => state.auth.userHasPermissions || [],
        }),

        hasAccess() {
            const permission = RESOURCE_PERMISSION_MAP[this.pageResource];
            return permission ? this.authUserPermissions.includes(permission) : false;
        },

        hasClientData() {
            const hasCol = this.pageColumns.some(col => col.field.includes('client'));
            return hasCol && this.table.clientOptions.length > 0 && this.companyCode === 'MIND-ID';
        },

        hasCompanyData() {
            const hasCol = this.pageColumns.some(col => col.field === 'company' || col.field === 'company_code');
            return hasCol && this.table.companyOptions.length > 0 && this.companyCode === 'MIND-ID';
        },

        hasPlantData() {
            const hasCol = this.pageColumns.some(col => col.field === 'plant' || col.field === 'plant_code');
            return hasCol && this.table.plantOptions.length > 0;
        },

        // Opsi filter
        clientFilterOptions() {
            const options = this.table.clientOptions.map(c => ({
                label: c.code || c.value || c.label || c,
                value: c.code || c
            }));
            options.unshift({ label: "All Clients", value: null });
            return options;
        },

        companyFilterOptions() {
            const options = this.table.companyOptions.map(c => ({
                label: c.code || c.name || c,
                value: c.code || c
            }));
            options.unshift({ label: "All Companies", value: null });
            return options;
        },

        plantFilterOptions() {
            const options = this.table.plantOptions.map(p => ({
                label: p.code || p.name || p,
                value: p.code || p
            }));
            options.unshift({ label: "All Plants", value: null });
            return options;
        },

        displayData() {
            return this.table.data;
        },
        pageResource() {
            return this.resource || this.$route.params.resource || this.$route.meta?.resource || "";
        },

        pageTitle() {
            if (this.$route.meta?.title) {
                return this.$route.meta.title;
            }

            if (this.pageResource) {
                return this.pageResource
                    .split("-")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
            }

            return "Master Data";
        },

        searchLabel() {
            return this.pageTitle.replace(/\s*List$/i, "").trim();
        },

        pageColumns() {
            // Saat halaman tidak punya data (mis. hasil filter kosong), jangan jatuh ke
            // SEMUA kolom universal — pakai kolom yang sudah dikenal resource ini dari load
            // sebelumnya yang ada datanya. Baru fallback ke UNIVERSAL_COLUMNS bila belum
            // pernah load sukses sama sekali.
            if (!this.table.data.length) {
                return this.table.knownColumns.length ? this.table.knownColumns : UNIVERSAL_COLUMNS;
            }
            return this.columnsFromRows(this.table.data);
        },
    },

    watch: {
        "table.page.current_pages": {
            handler() {
                this.get();
            },
        },
        // Watchers for filters
        "table.clientFilter": { handler() { this.syncUrlAndGet(); } },
        "table.companyFilter": { handler() { this.syncUrlAndGet(); } },
        "table.plantFilter": { handler() { this.syncUrlAndGet(); } },

        pageResource() {
            this.table.data = [];
            this.table.knownColumns = [];
            this.table.search = this.$route.query.search || "";
            this.table.clientFilter = this.$route.query.client || null;
            this.table.companyFilter = this.$route.query.company || null;
            this.table.plantFilter = this.$route.query.plant || null;
            this.table.page.current_pages = 1;
            this.get();
        },
    },

    mounted() {
				const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
				this.clientCode = userAuth?.user?.client_code || '';
				this.companyCode = userAuth?.user?.company_code || '';
        if (this.hasAccess) {
            this.getClients();
            this.getCompanies();
            this.getPlants();
            this.get();
        }
    },

    methods: {
        async getClients() {
            try {
                const response = await api(this, clientService.list({ limit: 1000 }), false);
                this.table.clientOptions = response.data?.data || [];
            } catch (error) {
                this.table.clientOptions = [];
            }
        },

        async getCompanies() {
            try {
                const response = await api(this, companiesService.list({ limit: 1000 }), false);
                this.table.companyOptions = response.data?.data || [];
            } catch (error) {
                this.table.companyOptions = [];
            }
        },

        async getPlants() {
            try {
                const response = await api(this, generalMasterDataService.list('plant', { limit: 1000 }), false);
                this.table.plantOptions = response.data?.data || response.data || [];
            } catch (error) {
                this.table.plantOptions = [];
            }
        },

        syncUrlAndGet() {
            this.$router.push({
                query: {
                    ...(this.table.search ? { search: this.table.search } : {}),
                    ...(this.table.clientFilter ? { client: this.table.clientFilter } : {}),
                    ...(this.table.companyFilter ? { company: this.table.companyFilter } : {}),
                    ...(this.table.plantFilter ? { plant: this.table.plantFilter } : {}),
                },
            });
            this.table.page.current_pages = 1;
            this.get();
        },

        async get() {
            if (!this.pageResource || !this.hasAccess) {
                this.table.onLoad = false;
                return;
            }

						this.table.onLoad = true;

            try {
                const response = await api(
                    this,
                    generalMasterDataService.list(this.pageResource, {
                        search: this.table.search,
                        client: this.companyCode === 'MIND-ID' ? this.table.clientFilter : this.clientCode,
                        company: this.table.companyFilter,
                        plant: this.table.plantFilter,
                        limit: this.table.limit,
                        page: this.table.page.current_pages,
                    }),
                    false
                );

                const paged = response.data || {};

                this.table.data = paged.data || [];

                // Ingat kolom yang dimiliki resource ini hanya dari respon yang ADA datanya,
                // supaya hasil filter kosong tidak menghapus/menggusur struktur kolomnya.
                if (this.table.data.length) {
                    this.table.knownColumns = this.columnsFromRows(this.table.data);
                }

                this.table.total = paged.total || this.table.data.length;
                this.table.page.total_pages = paged.last_page || 1;
                this.table.page.current_pages = paged.current_page || 1;
                this.table.page.per_pages = paged.per_page || this.table.limit;
            } catch (error) {
                this.table.data = [];
                this.table.total = 0;
                this.table.page.total_pages = 1;
            } finally {
                this.table.onLoad = false;
            }
        },

        filter() {
            clearTimeout(this.table.loadTimeout);
            this.table.loadTimeout = setTimeout(() => {
                this.syncUrlAndGet();
            }, 500);
        },

        changePage() {
            this.get();
        },

        async exportData() {
            if (!this.pageResource || this.exporting) return;

            this.exporting = true;
            try {
                const response = await generalMasterDataService.export(
                    this.pageResource.replace(/-/g, '_'),
                    this.companyCode === 'MIND-ID' ? this.table.clientFilter : this.clientCode
                );

                const timestamp = moment().format('YYYY-MM-DD-HH-mm');
                const filename = `${this.pageResource.replace(/-/g, '_')}_export_${timestamp}.xlsx`;

                const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                this.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Export berhasil!',
                    life: 3000
                });
            } catch (error) {
                console.error('Export error:', error);
                let message = 'Gagal mengekspor data. Silakan coba lagi.';
                if (error.response?.status === 404) {
                    message = 'Tipe data tidak ditemukan.';
                } else if (error.response?.status === 403) {
                    message = 'Anda tidak memiliki akses untuk mengekspor data ini.';
                } else if (error.response?.status === 500) {
                    message = 'Terjadi kesalahan pada server.';
                }

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: message,
                    life: 5000
                });
            } finally {
                this.exporting = false;
            }
        },

        // -----------------------------------------------------------------
        // Import Excel (pola sama dengan MCR: ImportExcelModal + store importExcel).
        // Service menerima (id, file) — id di sini = slug resource aktif.
        // -----------------------------------------------------------------
        openImportDialog() {
            if (!this.pageResource) return;
            this.importSuccessHandled = false;
            this.$store.dispatch('importExcel/openDialog', {
                config: {
                    title: `Import ${this.searchLabel}`,
                    acceptTypes: '.csv,.xls,.xlsx',
                    templateLabel: 'Download Template',
                    importLabel: 'Import',
                    cancelLabel: 'Cancel',
                    showTemplateButton: true,
                },
                id: this.pageResource,
                service: (resource, file) => generalMasterDataService.importData(resource, file),
                templateService: () => generalMasterDataService.downloadTemplate(this.pageResource),
            });
        },

        importServiceFn(resource, file) {
            return generalMasterDataService.importData(resource, file);
        },

        templateServiceFn() {
            return generalMasterDataService.downloadTemplate(this.pageResource);
        },

        async onImportSuccess() {
            if (this.importSuccessHandled) return;
            this.importSuccessHandled = true;
            const successMsg = this.$store.state.importExcel?.successMessage || 'Import completed successfully';
            this.$store.dispatch('importExcel/closeDialog');
            this.$toastNotifPrime('success', 'Success', successMsg);
            // Refresh tabel supaya data hasil import langsung tampil.
            this.table.page.current_pages = 1;
            this.get();
            this.importSuccessHandled = false;
        },

        onImportClose() {
            this.importSuccessHandled = false;
        },

        formatDate(date) {
            return moment(date).format("DD MMMM YYYY, HH:mm");
        },

        columnsFromRows(rows) {
            return UNIVERSAL_COLUMNS.filter((col) =>
                rows.some((row) => {
                    const v = this.getValueByPath(row, col.field);
                    return v !== undefined && v !== null && v !== "";
                })
            );
        },

        getValueByPath(obj, path) {
            if (!obj || !path) return undefined;
            return path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);
        },

        renderCell(row, col) {
            const raw = this.getValueByPath(row, col.field);

            if (col.type === "date") {
                return raw ? this.formatDate(raw) : (col.fallback ?? "");
            }

            if (raw === null || raw === undefined || raw === "") {
                return col.fallback ?? "";
            }

            return raw;
        },
    },
};
</script>

<style scoped>
:deep(.p-select-label) {
  display: flex;
  align-items: center;
  line-height: 0.5;
}
</style>
